import { useRef } from "react";

const DotCluster = ({ className = "", size = 300 }: { className?: string; size?: number }) => {
  const dots: { cx: number; cy: number; r: number; opacity: number }[] = [];
  const center = size / 2;

  for (let ring = 1; ring <= 6; ring++) {
    const radius = ring * (size / 14);
    const count = ring * 6;
    for (let i = 0; i < count; i++) {
      const angle = (i / count) * Math.PI * 2 + ring * 0.3;
      const jitter = Math.sin(i * 3.7 + ring) * 2;
      dots.push({
        cx: center + Math.cos(angle) * (radius + jitter),
        cy: center + Math.sin(angle) * (radius + jitter),
        r: 1.5 + Math.sin(i + ring * 2) * 0.8,
        opacity: 0.25 + Math.sin(i * 0.5 + ring) * 0.15,
      });
    }
  }

  return (
    <svg className={className} width={size} height={size} viewBox={`0 0 ${size} ${size}`} fill="none" aria-hidden="true">
      {dots.map((d, i) => (
        <circle key={i} cx={d.cx} cy={d.cy} r={d.r} fill="currentColor" opacity={d.opacity} />
      ))}
    </svg>
  );
};

const SerpentinePath = ({ className = "" }: { className?: string }) => (
  <svg className={className} width="100%" height="80" viewBox="0 0 1200 80" preserveAspectRatio="none" fill="none" aria-hidden="true">
    <path
      d="M0 40 C100 10, 200 70, 300 40 S500 10, 600 40 S800 70, 900 40 S1100 10, 1200 40"
      stroke="currentColor" strokeWidth="1" opacity="0.3" fill="none"
    />
    <path
      d="M0 44 C100 14, 200 74, 300 44 S500 14, 600 44 S800 74, 900 44 S1100 14, 1200 44"
      stroke="currentColor" strokeWidth="0.5" opacity="0.15" fill="none"
    />
    {[0, 150, 300, 450, 600, 750, 900, 1050, 1200].map((x, i) => (
      <circle key={i} cx={x} cy={40 + Math.sin(i * 1.2) * 20} r="2" fill="currentColor" opacity="0.25" />
    ))}
  </svg>
);

const ConcentricRings = ({ className = "", size = 260 }: { className?: string; size?: number }) => {
  const center = size / 2;

  return (
    <svg className={className} width={size} height={size} viewBox={`0 0 ${size} ${size}`} fill="none" aria-hidden="true">
      {[20, 35, 50, 65, 80, 95, 110].map((r, i) => (
        <circle
          key={r}
          cx={center}
          cy={center}
          r={r}
          stroke="currentColor"
          strokeWidth={0.6 + i * 0.1}
          fill="none"
          strokeDasharray={i % 2 === 0 ? "none" : `${3 + i} ${4 + i}`}
          opacity={0.08 + i * 0.03}
        />
      ))}
      {[0, 60, 120, 180, 240, 300].map((angle, i) => (
        <circle
          key={`center-${i}`}
          cx={center + Math.cos((angle * Math.PI) / 180) * 8}
          cy={center + Math.sin((angle * Math.PI) / 180) * 8}
          r="1.5"
          fill="currentColor"
          opacity="0.35"
        />
      ))}
      <circle cx={center} cy={center} r="2" fill="currentColor" opacity="0.4" />
    </svg>
  );
};

const BoomerangMotif = ({ className = "" }: { className?: string }) => (
  <svg className={className} width="160" height="80" viewBox="0 0 160 80" fill="none" aria-hidden="true">
    <path
      d="M15 65 Q40 5 80 35 Q120 65 145 15"
      stroke="currentColor" strokeWidth="2" opacity="0.3" fill="none" strokeLinecap="round"
    />
    <path
      d="M20 65 Q43 12 80 38 Q117 62 140 18"
      stroke="currentColor" strokeWidth="0.8" opacity="0.15" fill="none" strokeLinecap="round"
    />
    {[
      [15, 65], [35, 30], [55, 28], [80, 35], [105, 48], [125, 38], [145, 15]
    ].map(([x, y], i) => (
      <circle key={i} cx={x} cy={y} r="1.5" fill="currentColor" opacity="0.3" />
    ))}
  </svg>
);

const DotDivider = ({ className = "" }: { className?: string }) => (
  <div className={`flex items-center justify-center gap-2 py-2 ${className}`}>
    {Array.from({ length: 15 }).map((_, i) => (
      <div
        key={i}
        className="rounded-full bg-current"
        style={{
          width: `${3 + Math.sin(i * 0.8) * 1.5}px`,
          height: `${3 + Math.sin(i * 0.8) * 1.5}px`,
          opacity: 0.15 + Math.sin(i * 0.6) * 0.1,
        }}
      />
    ))}
  </div>
);

const WavyDotField = ({ className = "", rows = 5, cols = 20 }: { className?: string; rows?: number; cols?: number }) => (
  <svg className={className} width="100%" height={rows * 16} viewBox={`0 0 ${cols * 20} ${rows * 16}`} preserveAspectRatio="xMidYMid meet" fill="none" aria-hidden="true">
    {Array.from({ length: rows }).map((_, row) =>
      Array.from({ length: cols }).map((_, col) => {
        const xOffset = Math.sin(row * 1.2 + col * 0.3) * 3;
        const yOffset = Math.cos(col * 0.8 + row * 0.5) * 2;
        return (
          <circle
            key={`${row}-${col}`}
            cx={10 + col * 20 + xOffset}
            cy={8 + row * 16 + yOffset}
            r={1 + Math.sin(row + col * 0.7) * 0.6}
            fill="currentColor"
            opacity={0.1 + Math.sin(row * 0.9 + col * 0.4) * 0.08}
          />
        );
      })
    )}
  </svg>
);

export { DotCluster, SerpentinePath, ConcentricRings, BoomerangMotif, DotDivider, WavyDotField };
